import { EFinancingProgram } from '@if/core/types'
import {
  formatUtcIsoDateTime,
  getNumberArrayFromQueryString,
  getNumberFromQueryString,
  getStringArrayFromQueryString,
  getStringFromQueryString,
} from '@if/core/utils'
import { Button, Chip, Link, Stack } from '@mui/material'
import { DataGrid, GridColDef, GridFooter, GridFooterContainer } from '@mui/x-data-grid'
import { appSelectors } from '@src/data/store/AppStore'
import { useSideEffect } from '@src/data/store/effects/side-effects'
import { reportErrorToConsole } from '@src/services/error-logger'
import { omit } from 'lodash-es'
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import FullScreenContainer from '../../components/FullScreenContainer'
import { useAppSelector } from '../../data/store'
import {
  creditTaskDashboardEntryEffects,
  creditTaskDashboardEntrySelectors,
} from '../../data/store/CreditTaskDashboardEntry'
import { userSelectors } from '../../data/store/UserStore'
import {
  CreditApplicationReservation,
  CreditApplicationReservationDto,
  CreditTaskDashboardEntry,
  CreditTaskDashboardFilters,
  EEntryType,
  ValueListItem,
} from '../../data/types'
import CountStatuses from './components/CountStatuses'
import FilterComponent from './components/filters'
import CustomToolBar from './components/toolBar'

const DEFAULT_FILTER_VALUES = {
  limit: 50,
  offset: 0,
  financingProgramIds: [] as string[],
  entryType: [] as string[],
  territoryIds: [] as number[],
  assignation: 'blank',
  financingCompanyId: '' as string,
  correspondanceLanguageId: [] as number[],
  trusteeName: '',
  statesIso: [] as string[],
  dateRange: '',
}

const CreditTaskDashboardPage = () => {
  const { t } = useTranslation()
  const selectedCompany = useAppSelector(userSelectors.selectedCompany) as string
  const merchantTags = useAppSelector<ValueListItem[]>(appSelectors.getMerchantTags)
  const dispatchEffect = useSideEffect()

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { location } = window
  const updateSearch = new URLSearchParams(location.search)
  const [openSearchFilters, setOpenSearchFilters] = useState<boolean>(false)
  const filterValues = useMemo(() => {
    return {
      limit: getNumberFromQueryString(searchParams, 'limit', 50),
      offset: getNumberFromQueryString(searchParams, 'offset', 0),
      financingProgramIds: getStringArrayFromQueryString(searchParams, 'financingProgramIds'),
      loanPurposeIds: getStringArrayFromQueryString(searchParams, 'loanPurposeIds'),
      entryType: getStringArrayFromQueryString(searchParams, 'entryType'),
      territoryIds: getNumberArrayFromQueryString(searchParams, 'territoryIds'),
      assignation: getStringFromQueryString(searchParams, 'assignation', 'blank'),
      correspondanceLanguageId: getNumberArrayFromQueryString(searchParams, 'correspondanceLanguageId'),
      trusteeName: getStringFromQueryString(searchParams, 'trusteeName'),
      statesIso: getStringArrayFromQueryString(searchParams, 'statesIso'),
      dateRange: getStringFromQueryString(searchParams, 'dateRange'),
      financingCompanyId: selectedCompany,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const taskList = useAppSelector(creditTaskDashboardEntrySelectors.getTaskList)
  const statusCount = useAppSelector(creditTaskDashboardEntrySelectors.getStatusCount)
  const isLoading = useAppSelector(creditTaskDashboardEntrySelectors.isLoadingTaskList)
  const user = useAppSelector(userSelectors.getUser)
  const trusteeNamesWithEmptyOption = useAppSelector(creditTaskDashboardEntrySelectors.getTrusteeNamesWithEmptyOption)
  const userCanEditCreditApplication = user?.rights.canManageDocuments || user?.rights.canManageTasks

  useEffect(() => {
    dispatchEffect(creditTaskDashboardEntryEffects.getTrusteeNames()).catch(reportErrorToConsole)
  }, [dispatchEffect])

  useEffect(() => {
    const dates = filterValues.dateRange ? filterValues.dateRange.split('@') : ['', '']
    const endDate = dates[1] || formatUtcIsoDateTime(new Date())
    const startDate = dates[0]
    const queryParams = {
      ...omit(filterValues, ['dateRange']),
      startDate,
      endDate,
    }
    dispatchEffect(creditTaskDashboardEntryEffects.getTaskListByFilters(queryParams)).catch(reportErrorToConsole)
    dispatchEffect(creditTaskDashboardEntryEffects.getStatusCount(queryParams)).catch(reportErrorToConsole)
  }, [filterValues, dispatchEffect])

  const handleFilter = (newValue: CreditTaskDashboardFilters) => {
    updateSearch.set('limit', encodeURIComponent(newValue.limit))
    updateSearch.set('offset', encodeURIComponent(newValue.offset))
    updateSearch.set('financingProgramIds', encodeURIComponent(newValue.financingProgramIds.toString()))
    updateSearch.set('entryType', encodeURIComponent(newValue.entryType.toString()))
    updateSearch.set('correspondanceLanguageId', encodeURIComponent(newValue.correspondanceLanguageId.toString()))
    updateSearch.set('assignation', encodeURIComponent(newValue.assignation.toString()))
    updateSearch.set('trusteeName', encodeURIComponent(newValue.trusteeName.toString()))
    updateSearch.set('statesIso', encodeURIComponent(newValue.statesIso.toString()))
    updateSearch.set('financingCompanyId', encodeURIComponent(selectedCompany.toString()))
    updateSearch.set('dateRange', encodeURIComponent(newValue.dateRange))
    const newUrl = `${location.pathname}?${updateSearch.toString()}`
    navigate(newUrl, { replace: true })
  }

  const handleReserve = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      const values = e.currentTarget.value.split(',')
      const entityLockToAdd: CreditApplicationReservationDto = {
        financingProgramId: values[1] as EFinancingProgram,
        creditApplicationId: values[0],
        durationInMinutes: 20,
      }
      dispatchEffect(creditTaskDashboardEntryEffects.reserve(entityLockToAdd))
        .then((data: CreditApplicationReservation) => {
          if (data.isAcquired) window.open(`/Applications/${values[1]}/${data.creditApplicationId}/view`, '_blank')
        })
        .catch(reportErrorToConsole)
    },
    [dispatchEffect],
  )

  const handleBadgeClick = (entryType: EEntryType[]) => {
    handleFilter({ ...filterValues, entryType: [entryType.toString()] })
  }

  const columns = useMemo<GridColDef<CreditTaskDashboardEntry>[]>(
    () => [
      {
        field: 'creditApplicationReferenceNumber',
        headerName: t('browseCreditApplications.dataGridColumnHeaders.applicationNumber'),
        minWidth: 130,
        sortable: false,
        renderCell: (params) => {
          return (
            <div>
              <Link href={`/Applications/${params.row.financingProgramId}/${params.row.id}/view`}>
                {params.row.creditApplicationReferenceNumber}
              </Link>
              {params.row.isRush && <span style={{ color: 'red', fontWeight: 'bold' }}> ( ! )</span>}
            </div>
          )
        },
      },
      {
        field: 'reservedByUserId',
        headerName: t('taskManager.reservedBy'),
        minWidth: 130,
        sortable: false,
        renderCell: (params) => {
          if (params.row.reservedByUserId === null) {
            return (
              <Button
                variant="outlined"
                value={[params.row.id, params.row.financingProgramId]}
                onClick={handleReserve}
                disabled={!userCanEditCreditApplication}
              >
                {t('taskManager.reserve')}
              </Button>
            )
          }
          if (user?.id === params.row.reservedByUserId) {
            return t('taskManager.yourself')
          }
          return params.row.reservedByUserName
        },
      },
      { field: 'merchantName', headerName: t('taskManager.merchantName'), flex: 1, sortable: false },
      {
        field: 'financingProgramId',
        headerName: t('taskManager.financingProgram'),
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          return t(`EFinancingProgram.${params.row.financingProgramId}`, { ns: 'enum' })
        },
      },
      { field: 'clientName', headerName: t('taskManager.clientName'), flex: 1, sortable: false },
      { field: 'stateIso', headerName: 'Province', minWidth: 100, sortable: false },
      {
        field: 'tags',
        headerName: t('taskManager.tags'),
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Stack gap={0.5} direction="row" flexWrap="wrap">
            {params.row.tags?.map((tag) => (
              <Chip key={tag} size="small" label={merchantTags.find((mt) => mt.id === tag)?.text} />
            ))}
          </Stack>
        ),
      },
      {
        field: 'followUpReason',
        headerName: t('browseCreditApplications.dataGridColumnHeaders.followUpReason'),
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          return t(`taskManager.${params.row.actions[0]?.actionType}`)
        },
      },
    ],
    [t, user?.id, handleReserve, userCanEditCreditApplication, merchantTags],
  )
  const handleOnPageChange = (state: number) => {
    if (state === 0) {
      updateSearch.set('offset', encodeURIComponent(filterValues.offset - filterValues.limit))
    } else {
      updateSearch.set('offset', encodeURIComponent(filterValues.offset + filterValues.limit))
    }
    const newUrl = `${location.pathname}?${updateSearch.toString()}`
    navigate(newUrl, { replace: true })
  }

  const customToolBar = () => <CustomToolBar totalItems={taskList.length} />
  const customFooter = () => {
    return (
      <GridFooterContainer>
        <GridFooter sx={{ '& .MuiTablePagination-actions': { display: 'none' } }} />
        <div style={{ display: 'flex' }}>
          <Button onClick={() => handleOnPageChange(0)} disabled={filterValues.offset === 0}>
            {' '}
            {t('common.previous')}
          </Button>
          <Button onClick={() => handleOnPageChange(1)} disabled={taskList.length < filterValues.limit}>
            {' '}
            {t('common.next')}
          </Button>
        </div>
      </GridFooterContainer>
    )
  }
  const onCancel = useCallback(() => {
    setOpenSearchFilters(false)
  }, [setOpenSearchFilters])

  const paginationModel = useMemo(
    () => ({ pageSize: filterValues.limit, page: Math.floor(filterValues.offset / filterValues.limit) }),
    [filterValues.limit, filterValues.offset],
  )

  return (
    <FullScreenContainer>
      <FilterComponent
        filterValues={filterValues}
        resetFilter={() => handleFilter(DEFAULT_FILTER_VALUES)}
        applyFilter={handleFilter}
        trusteeNames={trusteeNamesWithEmptyOption}
        open={openSearchFilters}
        onCancel={onCancel}
      />

      <CountStatuses
        statusCount={statusCount}
        setOpenSearchFilters={setOpenSearchFilters}
        resetFilters={() => handleFilter(DEFAULT_FILTER_VALUES)}
        onBadgeClick={handleBadgeClick}
      />
      <div style={{ height: '36rem' }}>
        {taskList.length === 0 ? <h3> {t('common.noResultFound')} </h3> : ''}
        <DataGrid
          rows={taskList}
          columns={columns}
          checkboxSelection
          disableColumnFilter
          disableColumnMenu
          paginationModel={paginationModel}
          onPaginationModelChange={({ pageSize }) => handleFilter({ ...filterValues, limit: pageSize })}
          paginationMode="server"
          rowCount={taskList.length}
          loading={isLoading}
          slots={{
            toolbar: customToolBar,
            footer: customFooter,
          }}
        />
      </div>
    </FullScreenContainer>
  )
}

export default CreditTaskDashboardPage
